/**
 * The TSE candidate registry's read edge (platform design §11.4), `api`'s `routers/elections.py`.
 * `candidatureScopeSchema` mirrors `read_candidature_scope`, alongside `election`/`registration`'s
 * own routes.
 */

import { z } from 'zod';

import {
  campaignScopeCountSchema,
  campaignScopeLevelSchema,
  campaignScopeSchema,
  campaignUpdateSchema,
  electionSummarySchema,
  type CampaignScope,
  type CampaignScopeCount,
  type CampaignScopeLevel,
  CAMPAIGN_SCOPE_LEVELS,
} from '../../record/api/schemas';
import { itemsOf } from '../../../tenant/api/schemas';

export const electionSchema = electionSummarySchema.extend({
  // The offices this election's own roster actually holds -- the source for the Cargo select,
  // never a fixed enum, since a municipal election's roster differs from a state one's.
  offices: z.array(z.string()),
});
export type Election = z.infer<typeof electionSchema>;
export const electionsSchema = itemsOf(electionSchema);

export const registrationSchema = z.object({
  election_id: z.uuid(),
  tse_id: z.string(),
  ballot_name: z.string(),
  full_name: z.string(),
  office: z.string(),
  party: z.string().nullable(),
  coalition: z.string().nullable(),
  electoral_unit: z.string(),
  ballot_number: z.string(),
  // TSE's own wording ("DEFERIDO", "RENÚNCIA"): every candidature is offered, this tells them apart.
  registration_status: z.string(),
});
export type Registration = z.infer<typeof registrationSchema>;
export const registrationsSchema = itemsOf(registrationSchema);

/** One row of TSE's office table (`api`'s `TseOfficeOut`, `GET /tenants/{t}/tse/offices`): the
 *  closed list both picker modes choose Cargo from. `ballot_number_digits` is the length a
 *  candidate number for that office has. */
export const tseOfficeSchema = z.object({
  code: z.number().int(),
  name: z.string(),
  ballot_number_digits: z.number().int(),
});
export type TseOffice = z.infer<typeof tseOfficeSchema>;
export const tseOfficesSchema = itemsOf(tseOfficeSchema);

/** `GET /tenants/{t}/tse/units`: the units one election's roster holds for one office -- UF codes,
 *  or `BR` alone for a national office. */
export const tseUnitsSchema = itemsOf(z.string());

/** TSE publishes in capitals ("DEPUTADO FEDERAL", "AGUARDANDO JULGAMENTO"); a reader sees
 *  "Deputado federal", "Aguardando julgamento". */
export function sentenceCase(text: string): string {
  return text.charAt(0) + text.slice(1).toLocaleLowerCase('pt-BR');
}

export const officeLabel = sentenceCase;

/** The record's own `PUT` body: its candidate already carries the TSE identity pair. */
export const campaignCandidateLinkSchema = campaignUpdateSchema;
export type CampaignCandidateLink = z.infer<typeof campaignCandidateLinkSchema>;
export type LinkedCandidate = CampaignCandidateLink['candidate'];

/**
 * `GET /tenants/{t}/tse/candidates/scope` (`api`'s `read_candidature_scope`, `routers/
 * elections.py`): what a campaign for a picked candidature would cover, before the campaign
 * exists. `level` is the candidature's own scope (a national candidature's place is a country, a
 * gubernatorial one's a state, a mayoral one's a municipality); `counts` holds one entry per level
 * `core`'s `campaign_scope.COUNTED_LEVELS` rolls up under it -- one entry for a national or
 * municipal candidature, two for a state one.
 *
 * The wire shape is `api`'s one `ScopeOut`, the same the campaign record's own settled `scope`
 * parses with (`../../record/api/schemas`'s `campaignScopeSchema`), so this reuses it under this
 * module's own names rather than a second copy that could drift from it.
 */
export const LOCALITY_LEVELS = CAMPAIGN_SCOPE_LEVELS;
export const localityLevelSchema = campaignScopeLevelSchema;
export type LocalityLevel = CampaignScopeLevel;

export const localityCountSchema = campaignScopeCountSchema;
export type LocalityCount = CampaignScopeCount;

export const candidatureScopeSchema = campaignScopeSchema;
export type CandidatureScope = CampaignScope;

/** The ballot name carries the person; the office and party come from the roster row itself, so
 *  a registration always resolves to the same candidature whichever wins the search. */
export function candidateFromRegistration(registration: Registration): LinkedCandidate {
  return {
    name: registration.ballot_name,
    office: registration.office,
    ballot_number: registration.ballot_number,
    // A state or federal unit is its UF; a municipal one is TSE's municipality code, whose UF
    // `api` resolves on save (`_uf_of_unit`), as it re-reads office and number from the roster.
    uf:
      /^[A-Z]{2}$/.test(registration.electoral_unit) && registration.electoral_unit !== 'BR'
        ? registration.electoral_unit
        : null,
    party: registration.party,
    coalition: registration.coalition,
    tse_election_id: registration.election_id,
    tse_registration_id: registration.tse_id,
  };
}

/**
 * TSE's public "DivulgaCandContas" candidate page. ASSUMED: the wire contract carries no URL for
 * this -- built from `cycle_year`/`round` (the election) and `tse_id` (the registration's own TSE
 * sequential number), the three values that consulta route keys on. Confirm against the real
 * route once `fs-tse-candidate-link-read-routes` names one, if it ever does.
 */
export function tseRegistrationUrl(election: Election, registration: Registration): string {
  return `https://divulgacandcontas.tse.jus.br/divulga/#/candidato/${election.cycle_year}/${election.round}/${registration.electoral_unit}/${registration.tse_id}`;
}
